import { useRef, useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useChat } from '../context/ChatContext';
import { ScrollArea } from '../components/ui/scroll-area';
import MessageBubble from './MessageBubble';
import ThinkingIndicator from './ThinkingIndicator';

const FUNDLE_LOGO = 'https://customer-assets.emergentagent.com/job_fundle-ai/artifacts/tu7vfzht_erj2rwul_fundle_logo-white-min-382x100.png';

export default function ChatArea() {
  const { messages, isStreaming, currentThread, selectedBrand } = useChat();
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const [streamStartTime, setStreamStartTime] = useState(null);

  // Track when streaming starts
  useEffect(() => {
    if (isStreaming && !streamStartTime) {
      setStreamStartTime(Date.now());
    } else if (!isStreaming) {
      setStreamStartTime(null);
    }
  }, [isStreaming, streamStartTime]);

  // Auto scroll to bottom on new messages
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming]);

  // Empty state
  if (!currentThread) {
    return (
      <div className="flex-1 flex items-center justify-center p-8" data-testid="empty-state">
        <div className="text-center max-w-md">
          <img 
            src={FUNDLE_LOGO} 
            alt="Fundle" 
            className="h-8 mx-auto mb-6 opacity-50"
          />
          <h2 className="text-2xl font-bold mb-3">
            Welcome to Fundle GPT
          </h2>
          <p className="text-muted-foreground mb-6">
            Your AI-powered analytics assistant for loyalty programs. 
            Start a new chat to explore your data.
          </p>
          <div className="grid grid-cols-2 gap-3 text-left">
            <div className="p-4 rounded-xl bg-card/50 border border-border/50">
              <Sparkles className="w-5 h-5 text-primary mb-2" />
              <p className="text-sm font-medium">RFM Analysis</p>
              <p className="text-xs text-muted-foreground">Segment your customers</p>
            </div>
            <div className="p-4 rounded-xl bg-card/50 border border-border/50">
              <Sparkles className="w-5 h-5 text-secondary mb-2" />
              <p className="text-sm font-medium">Sales KPIs</p>
              <p className="text-xs text-muted-foreground">Revenue & performance</p>
            </div>
            <div className="p-4 rounded-xl bg-card/50 border border-border/50">
              <Sparkles className="w-5 h-5 text-accent mb-2" />
              <p className="text-sm font-medium">Cohort Analysis</p>
              <p className="text-xs text-muted-foreground">Retention insights</p>
            </div>
            <div className="p-4 rounded-xl bg-card/50 border border-border/50">
              <Sparkles className="w-5 h-5 text-yellow-500 mb-2" />
              <p className="text-sm font-medium">Loyalty Metrics</p>
              <p className="text-xs text-muted-foreground">Earn & burn analysis</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Messages
  return (
    <ScrollArea className="flex-1" ref={scrollRef}>
      <div className="max-w-3xl mx-auto px-4 pb-4" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              How can I help you today?
            </h3>
            <p className="text-muted-foreground">
              Ask me about your {selectedBrand?.name || 'brand'}'s loyalty data
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.message_id}
              message={message}
              isStreaming={message.isStreaming}
            />
          ))
        )}
        
        {/* Thinking indicator */}
        {isStreaming && messages.length > 0 && !messages[messages.length - 1]?.content && (
          <ThinkingIndicator isActive={true} startTime={streamStartTime} />
        )}
        
        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}
